import { FilePurpose, LanguagesEnum, languageValueToKey } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts/types';
import {
  CampaignMasterTemplateTranslation,
  CreateCampaignMasterTemplateRequest,
  phishingSignsLabels,
  UpdateCampaignMasterTemplateRequest,
} from '@/client/campaigns';
import { File as AppFile } from '@/client/files';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { phishingContentLanguages } from '@/common/constants/languages';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { PhishingTranslationSection } from '@/components/campaigns/master-templates';
import { BenchmarksSelectInput } from '@/components/form/selectors/BenchmarksSelectInput';
import {
  useCampaignMasterTemplate,
  useCreateCampaignMasterTemplate,
  useDisassociateCampaignTemplate,
  useSetPrimaryTemplate,
  useUpdateCampaignMasterTemplate,
} from '@/hooks/query';
import { usePhishingCharacteristics } from '@/hooks/query/phishing-characteristics.hooks';
import { usePhishingSigns } from '@/hooks/query/phishing-signs.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import {
  ContentLanguageTabs,
  useContentLanguageLabel,
} from '@/ui/content-language-tabs';
import { EditableDropdownItem } from '@/ui/editable-dropdown/EditableDropdown';
import { ImageUpload } from '@/ui/image-upload';
import { StarRating } from '@/ui/star-rating';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { isEmptyRichText } from '@/utils/helpers/rich-text.helper';
import { AxiosError } from 'axios';
import { Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// TODO: move to '@/ui/rich-text' — react-quill is being confined there
// so the editor can be swapped in one place.
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';

type FormValues = {
  translations: Record<string, { name: string; description: string }>;
  phishingSignId: string;
  characteristicIds: string[];
  benchmarkId: string;
  difficulty: number;
  thumbnailId: string;
  active: boolean;
};

const OuterWrapper = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  overflow: hidden;
  display: grid;
  grid-template-columns: 2fr 1fr;
`;

const LeftPanel = styled.div`
  background-color: var(--white-main);
  padding: var(--medium-big-padding);
  display: flex;
  flex-direction: column;
  gap: var(--medium-big-padding);
`;

const RightPanel = styled.div`
  background-color: var(--beige-main);
  padding: var(--medium-big-padding);
  display: flex;
  flex-direction: column;
  gap: var(--medium-big-padding);
  min-width: 320px;
`;

const SectionTitle = styled.div`
  font-size: var(--semi-big-font-size);
  font-weight: 600;
  color: var(--black-main);
  letter-spacing: 0.36px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--gray-dark);
  margin: 0;
`;

const SectionLabel = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  letter-spacing: 0.28px;
`;

const SectionSubtext = styled.div`
  font-size: var(--xsmall-font-size);
  font-weight: 600;
  color: var(--black-main);
  opacity: 0.5;
  letter-spacing: 0.24px;
`;

const LanguageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const LanguagePanel = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--box-border-radius);
  padding: var(--default-padding-small);
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

const LanguageTitle = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const RteWrapper = styled.div`
  background-color: var(--white-main);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding);
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);

  .quill {
    display: flex;
    flex-direction: column;
    gap: var(--xsmall-padding);
    background-color: transparent;
    border: none;
    min-height: auto;

    &:hover {
      border: none;
    }
  }

  .ql-toolbar.ql-snow {
    background-color: var(--white-main);
    border: 1px solid var(--gray-dark);
    border-bottom: 1px solid var(--gray-dark);
    border-radius: var(--xsmall-border-radius);
    padding: var(--medium-padding) var(--semi-big-font-size);
    text-align: left;
  }

  .ql-container.ql-snow {
    background-color: transparent;
    border: none;
    padding: 0;
    font-size: var(--medium-font-size);
  }

  .ql-editor {
    padding: 0;
    min-height: 80px;
    color: var(--black-main);
  }

  .quill .ql-container .ql-blank::before {
    left: 0;
    right: 0;
    color: var(--gray-darker);
    font-size: var(--medium-font-size);
    font-style: normal;
  }
`;

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const FieldLabel = styled.label`
  display: block;
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
  margin-bottom: var(--semi-xsmall-padding);

  .required {
    color: var(--red-main);
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
`;

const StyledInput = styled(InputText)`
  &.p-inputtext {
    background-color: var(--white-main);
    border-radius: var(--default-border-radius);
    min-height: var(--default-input-height);
    padding: var(--semi-medium-padding) var(--default-padding);
    border: none;
  }
`;

const StyledDropdown = styled(Dropdown)`
  &.p-dropdown {
    background-color: var(--beige-main);
    border-radius: var(--default-border-radius);
    min-height: var(--default-input-height);
    border: none;

    .p-dropdown-label {
      padding: var(--semi-medium-padding) var(--default-padding);
    }
  }
`;

const ErrorText = styled.small`
  color: var(--red-main);
  display: block;
  font-size: var(--xsmall-font-size);
`;

const DifficultyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--default-padding-small);
  align-items: end;
`;

const DifficultyPreview = styled.div`
  padding-bottom: var(--medium-padding);
`;

const EmptyStateContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--small-padding);
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 56px;
  height: 56px;
  border: 2px solid var(--black-main);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--heading-medium-font-size);
  color: var(--black-main);
`;

const EmptyStateTitle = styled.div`
  font-size: var(--medium-font-size);
  font-weight: 500;
  color: var(--black-main);
  letter-spacing: 0.32px;
`;

const EmptyStateText = styled.div`
  font-size: var(--small-font-size);
  font-weight: 400;
  color: var(--black-main);
  letter-spacing: 0.28px;
  max-width: 382px;
`;

const TemplateListItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--medium-padding);
  background-color: var(--white-main);
  border-radius: var(--box-border-radius);
  gap: var(--medium-padding);
`;

const ClickableLanguageSquare = styled.a<{ $primary: boolean }>`
  width: 54px;
  height: 54px;
  border-radius: var(--xsmall-padding);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--medium-font-size);
  font-weight: 600;
  letter-spacing: 0.32px;
  flex-shrink: 0;
  text-decoration: none;
  cursor: pointer;
  background-color: ${({ $primary }) =>
    $primary ? '#0e9797' : 'rgba(14, 151, 151, 0.2)'};
  color: ${({ $primary }) => ($primary ? 'var(--white-main)' : '#006161')};

  &:hover {
    opacity: 0.8;
  }
`;

const PillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
`;

const PrimaryPill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: var(--grand-border-radius);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  border: 1px solid var(--gray-dark);
  color: var(--gray-darker);
`;

const TemplateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4.5px;
  width: 55px;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--xxsmall-padding);
  color: var(--black-main);
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const StatusPill = styled.span<{ $live: boolean }>`
  display: inline-block;
  padding: 2px 9px;
  border-radius: var(--grand-border-radius);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  background-color: ${({ $live }) => ($live ? '#e6f5f0' : '#faf4de')};
  color: ${({ $live }) => ($live ? '#498778' : '#a16207')};
`;

const DIFFICULTY_MIN = 1;
const DIFFICULTY_MAX = 5;

const buildValidationSchema = (t: TranslationFunctionType) => {
  const difficultyRange = t('simulationTemplates.validation.difficultyRange', {
    min: DIFFICULTY_MIN,
    max: DIFFICULTY_MAX,
  });
  const allLanguagesRequired = t(
    'simulationTemplates.validation.allLanguagesRequired',
  );

  const translationsShape: Yup.ObjectShape = Object.fromEntries(
    phishingContentLanguages.map((language) => [
      language,
      Yup.object({
        name: Yup.string()
          .required(allLanguagesRequired)
          .test(
            'name-not-blank',
            allLanguagesRequired,
            (value) => !!value?.trim(),
          ),
        description: Yup.string()
          .required(allLanguagesRequired)
          .test(
            'description-not-empty',
            allLanguagesRequired,
            (value) => !isEmptyRichText(value),
          ),
      }),
    ]),
  );

  return Yup.object({
    translations: Yup.object(translationsShape),
    phishingSignId: Yup.string().required(
      t('simulationTemplates.validation.phishingSignRequired'),
    ),
    characteristicIds: Yup.array()
      .of(Yup.string().required())
      .min(1, t('simulationTemplates.validation.characteristicsRequired')),
    benchmarkId: Yup.string().required(
      t('simulationTemplates.validation.benchmarkRequired'),
    ),
    difficulty: Yup.number()
      .min(DIFFICULTY_MIN, difficultyRange)
      .max(DIFFICULTY_MAX, difficultyRange)
      .required(t('simulationTemplates.validation.difficultyRequired')),
    active: Yup.boolean(),
  });
};

const emptyTranslations = (): FormValues['translations'] =>
  phishingContentLanguages.reduce(
    (translations, language) => ({
      ...translations,
      [language]: { name: '', description: '' },
    }),
    {} as FormValues['translations'],
  );

const languageErrors = (
  errors: FormikErrors<FormValues>,
  language: LanguagesEnum,
): { name?: string; description?: string } =>
  (
    errors.translations as
      | Record<string, { name?: string; description?: string }>
      | undefined
  )?.[language] ?? {};

const languageTouched = (
  touched: FormikTouched<FormValues>,
  language: LanguagesEnum,
): { name?: boolean; description?: boolean } =>
  (
    touched.translations as
      | Record<string, { name?: boolean; description?: boolean }>
      | undefined
  )?.[language] ?? {};

const findFirstInvalidLanguage = (
  errors: FormikErrors<FormValues>,
): LanguagesEnum | undefined =>
  phishingContentLanguages.find(
    (language) => Object.keys(languageErrors(errors, language)).length > 0,
  );

const isTranslationEmpty = (
  translation?: FormValues['translations'][string],
): boolean =>
  !translation?.name?.trim() || isEmptyRichText(translation?.description);

const pathItems = (
  t: TranslationFunctionType,
  account: Account,
  pageTitle: string,
  id?: string,
) => [
  {
    label: account?.name,
    url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('simulationTemplates'),
    url: RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: pageTitle,
    url: id
      ? RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_EDIT](id)
      : RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_CREATE](),
    template: AppBreadCrumbTemplate,
  },
];

export const CampaignMasterTemplateFormPage: React.FC = () => {
  const { t } = useTranslation();
  const languageLabel = useContentLanguageLabel();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const currentAccount = useAppSelector(selectCurrentAccount);
  const toast = useToast();

  const { masterTemplate, isLoading: isLoadingTemplate } =
    useCampaignMasterTemplate(id ?? '', { enabled: isEdit } as any);
  const { phishingSigns } = usePhishingSigns();
  const { phishingCharacteristics } = usePhishingCharacteristics();
  const { create, isLoading: isCreating } = useCreateCampaignMasterTemplate();
  const { update, isLoading: isUpdating } = useUpdateCampaignMasterTemplate();
  const { disassociate } = useDisassociateCampaignTemplate();
  const { setPrimary } = useSetPrimaryTemplate();
  const { setDialogData } = useContext(DialogContext);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<LanguagesEnum>(
    phishingContentLanguages[0],
  );

  const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

  const thumbnailFile = useMemo(
    () => (masterTemplate?.thumbnail as AppFile) ?? null,
    [masterTemplate?.thumbnail],
  );

  const labels = useMemo(() => phishingSignsLabels(t), [t]);

  const phishingSignOptions = useMemo(
    () =>
      (phishingSigns?.result ?? []).map((sign) => ({
        label: labels[sign.key as keyof typeof labels] ?? sign.key,
        value: sign.id,
      })),
    [phishingSigns, labels],
  );

  const characteristicOptions = useMemo(
    () =>
      (phishingCharacteristics?.result ?? []).map((char) => ({
        label:
          char.translations?.find(
            (tr) => tr.language === LanguagesEnum.EN && tr.description,
          )?.description ??
          char.translations?.find((tr) => tr.description)?.description ??
          char.id,
        value: char.id,
      })),
    [phishingCharacteristics],
  );

  const difficultyOptions = [1, 2, 3, 4, 5].map((n) => ({
    label: `${n}`,
    value: n,
  }));

  const initialValues: FormValues = useMemo(() => {
    if (!masterTemplate) {
      return {
        translations: emptyTranslations(),
        phishingSignId: '',
        characteristicIds: [],
        benchmarkId: '',
        difficulty: 1,
        thumbnailId: '',
        active: true,
      };
    }

    const translations: FormValues['translations'] = emptyTranslations();
    for (const tr of masterTemplate.translations) {
      translations[tr.language] = {
        name: tr.name ?? '',
        description: tr.description ?? '',
      };
    }

    return {
      translations,
      phishingSignId: masterTemplate.phishingSign?.id ?? '',
      characteristicIds: masterTemplate.characteristics.map((c) => c.id),
      benchmarkId: masterTemplate.benchmark?.id ?? '',
      difficulty: masterTemplate.difficulty ?? 1,
      thumbnailId: masterTemplate.thumbnail?.id ?? '',
      active: masterTemplate.active,
    };
  }, [masterTemplate]);

  const handleSubmit = async (values: FormValues) => {
    const translationsPayload: CampaignMasterTemplateTranslation[] =
      Object.entries(values.translations).map(([lang, data]) => ({
        language: lang as LanguagesEnum,
        name: data.name || null,
        description: data.description || null,
      }));

    try {
      if (isEdit && id) {
        const request: UpdateCampaignMasterTemplateRequest = {
          translations: translationsPayload,
          phishingSignId: values.phishingSignId,
          characteristicIds: values.characteristicIds,
          benchmarkId: values.benchmarkId,
          difficulty: values.difficulty,
          active: values.active,
          thumbnailId: values.thumbnailId || null,
        };
        await update({ id, request });
        toast?.success(t('toast.success'), t('updateSuccess'));
      } else {
        const request: CreateCampaignMasterTemplateRequest = {
          translations: translationsPayload,
          phishingSignId: values.phishingSignId,
          characteristicIds: values.characteristicIds,
          benchmarkId: values.benchmarkId,
          difficulty: values.difficulty,
          active: values.active,
          thumbnailId: values.thumbnailId || undefined,
        };
        const result = await create(request);
        toast?.success(t('toast.success'), t('createSuccess'));
        if (result?.id) {
          navigate(
            RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_EDIT](
              result.id,
            ),
          );
        }
      }
    } catch (e) {
      const error = e as AxiosError;
      handleAxiosError(error, toast);
    }
  };

  const linkedTemplates = useMemo(
    () =>
      (masterTemplate?.templates ?? []) as Array<{
        id: string;
        name: string;
        language?: string;
        isPublished?: boolean;
      }>,
    [masterTemplate?.templates],
  );

  const primaryTemplateId = masterTemplate?.primaryTemplate ?? null;

  const handleUnlink = useCallback(
    (templateId: string) => {
      if (!id) return;
      setDialogData({
        type: DialogType.CONFIRMATION,
        show: true,
        header: t('simulationTemplates.unlinkConfirmTitle'),
        message: t('simulationTemplates.unlinkConfirmMessage'),
        onAccept: async () => {
          try {
            await disassociate({ masterTemplateId: id, templateId });
            toast?.success(t('toast.success'), t('updateSuccess'));
          } catch (e) {
            const error = e as AxiosError;
            handleAxiosError(error, toast);
          }
        },
      });
    },
    [id, disassociate, t, toast, setDialogData],
  );

  const handleSetPrimary = useCallback(
    async (templateId: string) => {
      if (!id) return;
      try {
        await setPrimary({ masterTemplateId: id, templateId });
        toast?.success(t('toast.success'), t('updateSuccess'));
      } catch (e) {
        const error = e as AxiosError;
        handleAxiosError(error, toast);
      }
    },
    [id, setPrimary, t, toast],
  );

  const enName =
    masterTemplate?.translations?.find((tr) => tr.language === LanguagesEnum.EN)
      ?.name ?? '';
  const pageTitle =
    isEdit && enName ? enName : t('simulationTemplates.newTemplate');

  if (isEdit && isLoadingTemplate) {
    return (
      <FlexContainer justify="center" className="p-4">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  return (
    <>
      <AppBreadCrumb
        model={pathItems(t, currentAccount as Account, pageTitle, id)}
      />

      <FlexContainer justify="space-between" align="center" className="mb-4">
        <h1 style={{ margin: 0 }}>{pageTitle}</h1>
        <FlexContainer gap="var(--small-padding)" width="auto">
          <AppButton
            label={t('cancel')}
            type="outlined"
            onClick={() =>
              navigate(
                RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES](),
              )
            }
          />
          <AppButton
            label={t('save')}
            isDisabled={isCreating || isUpdating || isUploadDisabled}
            onClick={() =>
              document
                .getElementById('master-template-form')
                ?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                )
            }
          />
        </FlexContainer>
      </FlexContainer>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik: FormikProps<FormValues>) => (
          <form
            id="master-template-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const errors = await formik.validateForm();
              const invalidLanguage = findFirstInvalidLanguage(errors);
              if (invalidLanguage) {
                setActiveLanguage(invalidLanguage);
              }
              formik.handleSubmit();
            }}
          >
            <OuterWrapper>
              <LeftPanel>
                <FlexContainer justify="space-between" align="center">
                  <SectionTitle>{t('templateSettings')}</SectionTitle>
                  <AppSwitch
                    checked={formik.values.active}
                    onChange={(e) => formik.setFieldValue('active', e.value)}
                    withText
                  />
                </FlexContainer>

                <Divider />

                <LanguageSection>
                  <SectionLabel>
                    {t('simulationTemplates.adminUILanguages')}
                  </SectionLabel>

                  <ContentLanguageTabs
                    languages={phishingContentLanguages}
                    active={activeLanguage}
                    onChange={setActiveLanguage}
                    isEmpty={(language) =>
                      isTranslationEmpty(formik.values.translations[language])
                    }
                    hasError={(language) =>
                      formik.submitCount > 0 &&
                      Object.keys(languageErrors(formik.errors, language))
                        .length > 0
                    }
                  />

                  <LanguagePanel>
                    <LanguageTitle>
                      {languageLabel(activeLanguage)}
                    </LanguageTitle>
                    <FieldGroup>
                      <FieldLabel>
                        {t('name')}
                        <span className="required">*</span>
                      </FieldLabel>
                      <StyledInput
                        name={`translations.${activeLanguage}.name`}
                        value={
                          formik.values.translations[activeLanguage]?.name ?? ''
                        }
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={t('simulationTemplates.namePlaceholder')}
                        className="w-full"
                      />
                      {languageTouched(formik.touched, activeLanguage).name &&
                        languageErrors(formik.errors, activeLanguage).name && (
                          <ErrorText>
                            {languageErrors(formik.errors, activeLanguage).name}
                          </ErrorText>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                      <FieldLabel>
                        {t('simulationTemplates.simulationDescription')}
                        <span className="required">*</span>
                      </FieldLabel>
                      <RteWrapper>
                        <ReactQuill
                          key={activeLanguage}
                          value={
                            formik.values.translations[activeLanguage]
                              ?.description ?? ''
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `translations.${activeLanguage}.description`,
                              value,
                            )
                          }
                          onBlur={() =>
                            formik.setFieldTouched(
                              `translations.${activeLanguage}.description`,
                              true,
                            )
                          }
                          modules={quillModules}
                          placeholder={t(
                            'simulationTemplates.descriptionPlaceholder',
                          )}
                        />
                      </RteWrapper>
                      {languageTouched(formik.touched, activeLanguage)
                        .description &&
                        languageErrors(formik.errors, activeLanguage)
                          .description && (
                          <ErrorText>
                            {
                              languageErrors(formik.errors, activeLanguage)
                                .description
                            }
                          </ErrorText>
                        )}
                    </FieldGroup>
                  </LanguagePanel>
                </LanguageSection>

                <Divider />

                <FieldGroup>
                  <FieldLabel>
                    {t('benchmark')}
                    <span className="required">*</span>
                  </FieldLabel>
                  <BenchmarksSelectInput
                    value={formik.values.benchmarkId}
                    placeholder={t('simulationTemplates.enterBenchmarkId')}
                    onChange={(item: EditableDropdownItem | null) =>
                      formik.setFieldValue('benchmarkId', item?.id ?? '')
                    }
                  />
                  {formik.touched.benchmarkId && formik.errors.benchmarkId && (
                    <ErrorText>{formik.errors.benchmarkId}</ErrorText>
                  )}
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel>
                    {t('phishingSign')}
                    <span className="required">*</span>
                  </FieldLabel>
                  <StyledDropdown
                    name="phishingSignId"
                    value={formik.values.phishingSignId}
                    options={phishingSignOptions}
                    onChange={(e) =>
                      formik.setFieldValue('phishingSignId', e.value)
                    }
                    placeholder={t('simulationTemplates.selectPhishingSign')}
                    className="w-full"
                  />
                  {formik.touched.phishingSignId &&
                    formik.errors.phishingSignId && (
                      <ErrorText>{formik.errors.phishingSignId}</ErrorText>
                    )}
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel>
                    {t('phishingCharacteristics')}
                    <span className="required">*</span>
                  </FieldLabel>
                  <MultiSelect
                    name="characteristicIds"
                    value={formik.values.characteristicIds}
                    options={characteristicOptions}
                    onChange={(e) =>
                      formik.setFieldValue('characteristicIds', e.value)
                    }
                    placeholder={t('simulationTemplates.addCharacteristic')}
                    display="chip"
                    className="w-full"
                  />
                  {formik.touched.characteristicIds &&
                    formik.errors.characteristicIds && (
                      <ErrorText>
                        {formik.errors.characteristicIds as string}
                      </ErrorText>
                    )}
                </FieldGroup>

                <DifficultyRow>
                  <FieldGroup>
                    <FieldLabel>
                      {t('difficulty')}
                      <span className="required">*</span>
                    </FieldLabel>
                    <StyledDropdown
                      name="difficulty"
                      value={formik.values.difficulty}
                      options={difficultyOptions}
                      onChange={(e) =>
                        formik.setFieldValue('difficulty', e.value)
                      }
                      className="w-full"
                    />
                    {formik.touched.difficulty && formik.errors.difficulty && (
                      <ErrorText>{formik.errors.difficulty}</ErrorText>
                    )}
                  </FieldGroup>
                  <DifficultyPreview>
                    <StarRating
                      value={formik.values.difficulty}
                      maxStars={5}
                      icon="fish"
                      disabled
                    />
                  </DifficultyPreview>
                </DifficultyRow>

                <ImageUpload
                  label={t('simulationTemplates.coverImage')}
                  purpose={FilePurpose.THUMBNAIL}
                  preselectedFile={thumbnailFile}
                  onFileUpload={(file: AppFile | null) =>
                    formik.setFieldValue('thumbnailId', file?.id ?? '')
                  }
                  setIsSubmitDisabled={setIsUploadDisabled}
                />
              </LeftPanel>

              <RightPanel>
                <FlexContainer justify="space-between" align="center">
                  <SectionTitle>{t('simulationLanguageVariants')}</SectionTitle>
                  <SectionSubtext>
                    {t('simulationTemplates.languageCount', {
                      count: linkedTemplates.length,
                    })}
                  </SectionSubtext>
                </FlexContainer>

                <Divider />

                {linkedTemplates.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--small-padding)',
                    }}
                  >
                    {linkedTemplates.map((template) => {
                      const isPrimary = template.id === primaryTemplateId;
                      const langCode = template.language
                        ? languageValueToKey[template.language] ??
                          template.language
                        : '';
                      return (
                        <TemplateListItem key={template.id}>
                          <ClickableLanguageSquare
                            $primary={isPrimary}
                            href={RedirectPaths[
                              RedirectPathsEnum.CAMPAIGNS_EDIT
                            ](template.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              navigate(
                                RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](
                                  template.id,
                                ),
                                { state: { isTemplate: true } },
                              );
                            }}
                          >
                            {langCode}
                          </ClickableLanguageSquare>
                          <PillsRow>
                            <StatusPill $live={!!template.isPublished}>
                              {template.isPublished
                                ? t('simulationTemplates.status.live')
                                : t('simulationTemplates.status.draft')}
                            </StatusPill>
                            {isPrimary && (
                              <PrimaryPill>
                                {t('simulationTemplates.primary')}
                              </PrimaryPill>
                            )}
                          </PillsRow>
                          <TemplateActions>
                            <IconButton
                              type="button"
                              title={t('simulationTemplates.setPrimary')}
                              onClick={() =>
                                !isPrimary && handleSetPrimary(template.id)
                              }
                              style={{
                                cursor: isPrimary ? 'default' : 'pointer',
                                opacity: isPrimary ? 1 : undefined,
                              }}
                            >
                              <i
                                className={
                                  isPrimary ? 'pi pi-star-fill' : 'pi pi-star'
                                }
                                style={{ color: '#006161' }}
                              />
                            </IconButton>
                            <IconButton
                              type="button"
                              title={t('simulationTemplates.unlinkTemplate')}
                              onClick={() => handleUnlink(template.id)}
                            >
                              <i className="pi pi-trash" />
                            </IconButton>
                          </TemplateActions>
                        </TemplateListItem>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyStateContainer>
                    <EmptyStateIcon>?</EmptyStateIcon>
                    <EmptyStateTitle>
                      {t('simulationTemplates.noSimulationsChosen')}
                    </EmptyStateTitle>
                    <EmptyStateText>
                      {t('simulationTemplates.noSimulationsDescription')}
                    </EmptyStateText>
                  </EmptyStateContainer>
                )}

                {isEdit && id && (
                  <PhishingTranslationSection
                    masterTemplateId={id}
                    primaryTemplateId={primaryTemplateId}
                    templates={linkedTemplates}
                  />
                )}
              </RightPanel>
            </OuterWrapper>
          </form>
        )}
      </Formik>
    </>
  );
};
